// ═══════════════════════════════════════════════════════════════
//  SATELLITE PLAYBOOK — DESIGN SYSTEM
//  Edit this file to change colors, fonts, and component styles.
//  Professional aerospace/engineering aesthetic.
// ═══════════════════════════════════════════════════════════════

// ── Font stacks ──────────────────────────────────────────────
export const FONT = {
  sans: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Courier New', monospace",
};

// ── Color tokens ─────────────────────────────────────────────
// Background layers (darkest → lightest)
export const T = {
  bg0: "#0d1117",   // page canvas
  bg1: "#161b22",   // card / panel
  bg2: "#1c2128",   // inner cell / row alt

  // Border tones
  b0: "#21262d",
  b1: "#30363d",
  b2: "#484f58",

  // Brand & status colors
  acc: "#2f81f7",   // primary blue
  grn: "#3fb950",   // success / positive
  yel: "#d29922",   // warning / amber
  red: "#f85149",   // danger / negative
  pur: "#a371f7",   // purple / highlight
  org: "#e3b341",   // gold / caution

  // Text hierarchy
  t0: "#e6edf3",    // primary
  t1: "#8b949e",    // secondary
  t2: "#484f58",    // muted / disabled
};

// ── Subsystem palette — desaturated, professional ────────────
export const SC = {
  struct:  "#e8975a",   // warm orange
  eps:     "#c9a227",   // gold
  comms:   "#4493f8",   // blue
  adcs:    "#3fb950",   // green
  obc:     "#a371f7",   // purple
  thermal: "#db61a2",   // rose
  payload: "#60a5fa",   // sky blue
  prop:    "#56d364",   // light green
  harness: "#9a7fbd",   // muted violet
};

// ── Typography helpers ────────────────────────────────────────
// Use `s` (sans) for labels/headings, `m` (mono) for values/codes
export const s = { fontFamily: FONT.sans };
export const m = { fontFamily: FONT.mono };

// ── Reusable component style objects ─────────────────────────
// These are the building blocks consumed by the component.
// Change padding, radius, borders here once and it applies everywhere.

export const CARD = {
  background: T.bg1,
  border: `1px solid ${T.b1}`,
  borderRadius: 8,
  padding: "20px 24px",
};

export const CARD_INNER = {
  background: T.bg2,
  border: `1px solid ${T.b0}`,
  borderRadius: 6,
  padding: "12px 16px",
};

export const SECTION_LABEL = {
  fontFamily: FONT.sans,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: T.t1,
  marginBottom: 16,
};

export const VALUE_LARGE = {
  fontFamily: FONT.mono,
  fontSize: 20,
  fontWeight: 700,
  color: T.t0,
};

export const VALUE_MEDIUM = {
  fontFamily: FONT.mono,
  fontSize: 13,
  fontWeight: 600,
};

export const LABEL_SM = {
  fontFamily: FONT.sans,
  fontSize: 11,
  fontWeight: 500,
  color: T.t1,
};

export const LABEL_XS = {
  fontFamily: FONT.sans,
  fontSize: 10,
  color: T.t2,
};

// Table header / cell base styles
export const TH_STYLE = {
  fontFamily: FONT.sans,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: T.t1,
  padding: "10px 12px",
  textAlign: "left",
  borderBottom: `1px solid ${T.b1}`,
  background: T.bg1,
  whiteSpace: "nowrap",
};

export const TD_STYLE = {
  fontFamily: FONT.sans,
  fontSize: 12,
  color: T.t0,
  padding: "9px 12px",
  borderBottom: `1px solid ${T.b0}`,
  verticalAlign: "top",
};

// Input / select common style
export const INPUT_STYLE = {
  background: T.bg2,
  border: `1px solid ${T.b1}`,
  borderRadius: 6,
  padding: "7px 10px",
  fontFamily: FONT.sans,
  fontSize: 13,
  color: T.t0,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

// Progress / gauge bar track
export const BAR_TRACK = {
  height: 4,
  background: T.b1,
  borderRadius: 3,
  overflow: "hidden",
};

// Tab button base style — active/inactive toggled in component
export const TAB_BASE = {
  padding: "11px 18px",
  fontSize: 12,
  fontFamily: FONT.sans,
  fontWeight: 500,
  cursor: "pointer",
  background: "transparent",
  border: "none",
  transition: "all 0.12s",
  whiteSpace: "nowrap",
};

// Badge / chip
export const chip = (color) => ({
  fontFamily: FONT.sans,
  fontSize: 10,
  fontWeight: 600,
  color,
  background: `${color}18`,
  border: `1px solid ${color}40`,
  padding: "2px 8px",
  borderRadius: 4,
  letterSpacing: "0.04em",
});

// Status indicator dot
export const dot = (color, size = 8) => ({
  width: size,
  height: size,
  borderRadius: "50%",
  background: color,
  flexShrink: 0,
});

// Stat card (header banner tiles)
export const STAT_CARD = {
  background: T.bg1,
  border: `1px solid ${T.b1}`,
  borderRadius: 6,
  padding: "10px 16px",
  minWidth: 90,
};
